import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import PaginateList from "../../../utils/PaginateList";
import * as WorkingPartServices from "../../../../services/WorkingPartService";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PATHS from "../../../../constants/paths";
import moment from "moment-timezone";

import {
  ButtonAction,
  ButtonClose,
  Icon,
  Option,
  ListOptions,
} from "./workingPartList.styled";
import { Can, AbilityContext } from "../../../security/permissions/Can";
import { USER_PERMISSIONS } from "../../../security/permissions/rolesPermission";
import {
  checkBootstrapSizeBreak,
  SIZES,
} from "../../../../utils/device/checkDevice";
import { ShowAlertDialog } from "@de-prado-dev/react-component-library";
import { useDailyPart } from "../hook/useDailyPart";
import { useDailyPartEditDate } from "../hook/useDailyPartEditDate";
import { converToTime } from "../../../../utils/formats/number";
import {
  PART_STATUS,
  STATUS_PART_PENDING,
  STATUS_PART_VALIDATED,
} from "../../../../constants/index";

const WorkingPartListContainer = ({ filters, sortBy, isTechnical }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const deviceSize = checkBootstrapSizeBreak();
  const [forceRefresh, setForceRefresh] = useState(false);
  const context = useContext(AbilityContext);
  const refresh = () => {
    setForceRefresh(true);
  };

  const { createPart, createAdminDailyPart, validateDailyPart } =
    useDailyPart(refresh);

  const { editDailyPart } = useDailyPartEditDate(refresh);

  const getColumns = () => {
    const columns = [];
    if (!isTechnical) {
      columns.push({
        header: t("forms.workingPart.table.heads.user"),
        accessorKey: "user.name",
        cell: (info) => `${info.getValue()} ${info.row.original.user.surname}`,
      });
    }
    columns.push({
      header: t("forms.workingPart.table.heads.date"),
      accessorKey: "date",
      cell: (info) => moment(info.getValue()).format("DD-MM-yyyy"),
    });
    columns.push({
      header: t("forms.workingPart.table.heads.totalTime"),
      accessorKey: "totalTime",
      cell: (info) => (info.getValue() ? converToTime(info.getValue()) : "-"),
    });
    columns.push({
      header: t("forms.workingPart.table.heads.status"),
      accessorKey: "status",
      cell: (info) => {
        const status = PART_STATUS.find((st) => st.code === info.getValue());
        if (status) {
          return t(status.label);
        }

        return info.getValue();
      },
    });
    columns.push({
      Header: " ",
      accessorKey: " ",
      cell: (info) => renderAction(info.row.original),
    });

    return columns;
  };

  const goToEdit = (uuid) => {
    navigate(`/user/workingPart/form/update/${uuid}`);
  };

  const showPart = (uuid) => {
    if (isTechnical) {
      navigate(`/technical/workingPart/detail/${uuid}`);
    } else {
      navigate(`/user/workingPart/detail/${uuid}`);
    }
  };

  const handlerValidateDailyPart = async (uuid) => {
    try {
      await validateDailyPart(uuid);
    } catch (error) {}
  };

  const handlerEditDailyPart = (uuid) => {
    editDailyPart(uuid);
  };

  const renderAction = (data) => {
    return (
      <div className="text-center">
        <Can do={USER_PERMISSIONS.ADM_CREATE_PART}>
          {!isTechnical && data.status === STATUS_PART_PENDING.code && (
            <ButtonAction
              onClick={() => handlerEditDailyPart(data.uuid)}
              type="button"
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </ButtonAction>
          )}
        </Can>
        <ButtonAction onClick={() => showPart(data.uuid)} type="button">
          <i className="fa-solid fa-arrow-right"></i>
        </ButtonAction>
        {data.status === STATUS_PART_PENDING.code && (
          <>
            <Can do={USER_PERMISSIONS.PART_VALIDATE}>
              {!isTechnical && data.status !== STATUS_PART_VALIDATED.code && (
                <ButtonAction
                  type="button"
                  onClick={async () => handlerValidateDailyPart(data.uuid)}
                >
                  <i className="fa-solid fa-calendar-check"></i>
                </ButtonAction>
              )}
            </Can>
            <Can do={USER_PERMISSIONS.DELETE_PART}>
              {!isTechnical && (
                <ButtonAction
                  onClick={() => showDialogOnDelete(data.uuid)}
                  type="button"
                  $isDelete
                >
                  <i className="fa-solid fa-trash-can"></i>
                </ButtonAction>
              )}
            </Can>
          </>
        )}
      </div>
    );
  };

  const showDialogOnDelete = (uuid) => {
    const okAction = async () => {
      WorkingPartServices.deleteDailyWorkingPart(uuid).then((result) => {
        if (result) {
          setForceRefresh(true);
        }
      });
    };

    const cancelAction = () => {};

    ShowAlertDialog(
      t("forms.workingPart.dialog.remove.title"),
      t("forms.workingPart.dialog.remove.text"),
      "warning",
      true,
      t("forms.workingPart.dialog.remove.buttonOk"),
      false,
      okAction,
      t("forms.workingPart.dialog.remove.buttonNo"),
      cancelAction
    );
  };

  const goToForm = async () => {
    createPart(isTechnical);
  };

  const goToAdminForm = async () => {
    createAdminDailyPart();
  };

  const listData = async (filter) => {
    if (sortBy) {
      filter.sort = sortBy;
    } else {
      filter.sort = "-date";
    }
    const result = await WorkingPartServices.listFilter(filter, isTechnical);
    return result;
  };

  const hideColumn = {};
  let resize = null;

  if (deviceSize < SIZES.md) {
    // hideColumn.itemCode = false;
    resize = false;
  } else if (deviceSize === SIZES.md) {
    // hideColumn.itemCode = false;
  }

  return (
    <>
      <PaginateList
        refresh={forceRefresh}
        filters={filters}
        columns={getColumns()}
        toList={listData}
        hideColum={hideColumn}
        resize={resize}
        resetRefresh={() => setForceRefresh(false)}
      ></PaginateList>

      {isTechnical && (
        <div className="row justify-content-center mt-4">
          <div className="col-12 col-md-11 d-flex justify-content-center">
            <button
              className="btn btn-secondary btn-md"
              onClick={() => goToForm()}
            >
              {t("forms.workingPart.buttons.new")}
            </button>
          </div>
        </div>
      )}
      <Can do={USER_PERMISSIONS.ADM_CREATE_PART}>
        <div className="row justify-content-center mt-4">
          <div className="col-12 col-md-11 d-flex justify-content-center">
            <button
              className="btn btn-secondary btn-md"
              onClick={() => goToAdminForm()}
            >
              {t("forms.workingPart.buttons.newDaily")}
            </button>
          </div>
        </div>
      </Can>
    </>
  );
};

WorkingPartListContainer.propTypes = {
  filters: PropTypes.object,
};

export default WorkingPartListContainer;
